import { get } from "radash"

import { getPartnerAccessTokenFromCode, getPartnerCode } from "@be-miniapp/api"
import {
  invokeBackendCall,
  log,
  recordFlowStep,
  serializeError,
} from "@be-miniapp/logging"

import { registerPlatform, type PlatformAdapters } from "../index"
import type {
  AuthCodeParams,
  AuthResult,
  Coordinates,
  LocationOptions,
  NetworkStatus,
  PermissionSettings,
  PermissionStatus,
  SystemInfo,
} from "../types"

function summarizePhoneInput(phoneNo: string) {
  const cleaned = phoneNo.replace(/\D/g, "")
  return {
    inputLength: phoneNo.length,
    digitCount: cleaned.length,
    startsWithPlus: phoneNo.startsWith("+"),
    cleanedPrefix: cleaned.slice(0, 3) || undefined,
    cleanedSuffix: cleaned.slice(-2) || undefined,
  }
}

const validatePhoneNumber = (phoneNo: string): string => {
  const cleaned = phoneNo.replace(/\D/g, "")

  // Handle Vietnamese phone numbers
  if (cleaned.startsWith("0") && cleaned.length === 10) {
    return `+84${cleaned.slice(1)}`
  }

  // Handle international format
  if (cleaned.startsWith("84") && cleaned.length === 11) {
    return `+${cleaned}`
  }

  // Return as-is if already in correct format
  if (phoneNo.startsWith("+84") && phoneNo.length === 12) {
    return phoneNo
  }

  const input = summarizePhoneInput(phoneNo)
  const error = new Error(
    `Invalid phone number format: digitCount=${input.digitCount}, inputLength=${input.inputLength}, startsWithPlus=${input.startsWithPlus}`
  )
  ;(error as Error & { phoneInput: typeof input }).phoneInput = input
  throw error
}

function summarizeAuthUrlParams(searchParams: URLSearchParams) {
  return {
    hasCode: searchParams.has("code"),
    hasCodeVerifier: searchParams.has("code_verifier"),
    hasPhoneNo: searchParams.has("phone_no"),
    partnerId: searchParams.get("partner_id") ?? undefined,
  }
}

const AUTH_BACKEND_ENDPOINTS = {
  partnerAuthorize: "api/v1/be-web-gateway/partner/be/authorize",
  accessToken: "api/v1/be-web-gateway/public/v1/auth/access-token",
} as const

function summarizeOAuthTokenRequest(code: string, codeVerifier?: string) {
  return {
    codePrefix: `${code.substring(0, 6)}…`,
    codeLength: code.length,
    hasCodeVerifier: Boolean(codeVerifier),
  }
}

function summarizePartnerAuthorizeRequest(input: {
  partner_id: number
  partner_name: string
  partner_secret: string
  user: {
    phone_no: string
    fullname: string
    user_id: string
  }
}) {
  return {
    partner_id: input.partner_id,
    partner_name: input.partner_name,
    hasPartnerSecret: Boolean(input.partner_secret),
    user: {
      fullname: input.user.fullname,
      phoneSuffix: input.user.phone_no.slice(-4),
    },
  }
}

async function exchangePartnerAccessToken(
  flow: string,
  code: string,
  codeVerifier?: string
) {
  return invokeBackendCall(
    {
      feature: "auth",
      step: "api.exchange_access_token",
      endpoint: AUTH_BACKEND_ENDPOINTS.accessToken,
      flow,
      request: summarizeOAuthTokenRequest(code, codeVerifier),
    },
    () => getPartnerAccessTokenFromCode(code, codeVerifier)
  )
}

async function requestPartnerAuthorizeCode(
  flow: string,
  payload: Parameters<typeof getPartnerCode>[0]
) {
  return invokeBackendCall(
    {
      feature: "auth",
      step: "api.get_partner_code",
      endpoint: AUTH_BACKEND_ENDPOINTS.partnerAuthorize,
      flow,
      request: summarizePartnerAuthorizeRequest(payload),
    },
    () => getPartnerCode(payload)
  )
}

function recordAuthApiStep(
  step: string,
  data?: Record<string, unknown>,
  level: "info" | "warning" | "error" = "info"
) {
  recordFlowStep({
    feature: "auth",
    category: "auth-api",
    step,
    data: { platform: "appview", ...data },
    level,
  })
}

function captureAuthApiFailure(
  step: string,
  error: unknown,
  extra?: Record<string, unknown>
): never {
  recordFlowStep({
    feature: "auth",
    category: "auth-api",
    step,
    data: {
      platform: "appview",
      ...extra,
      error: serializeError(error),
    },
    level: "error",
  })
  throw error
}

function parseAccessToken(value: unknown): string {
  if (typeof value !== "string") {
    throw new Error("No access token received")
  }

  const trimmed = value.trim()
  if (!trimmed || trimmed === "null" || trimmed === "undefined") {
    throw new Error("No access token received")
  }

  return trimmed
}

export const appViewPlatform: PlatformAdapters = {
  navigator: {
    navigate: (to: string) => {
      window.location.assign(to)
    },
    back: () => {
      window.history.back()
    },
  },

  phone: {
    call: async (phoneNumber: string) => {
      window.location.href = `tel:${phoneNumber}`
    },
  },

  device: {
    getDeviceId: async (): Promise<string> => {
      let deviceId = localStorage.getItem("be:tracking:id")
      if (!deviceId) {
        deviceId = Math.floor(Math.random() * Date.now()).toString(16)
        localStorage.setItem("be:tracking:id", deviceId)
      }
      return deviceId
    },

    getSystemInfo: async (): Promise<SystemInfo> => {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
      const isAndroid = /Android/i.test(navigator.userAgent)

      return {
        platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        version: navigator.userAgent,
      }
    },

    vibrate: async () => {
      // Use Vibration API if available
      if ("vibrate" in navigator) {
        navigator.vibrate(200)
      }
    },
  },

  auth: {
    authenticateWithCode: async (
      code: string,
      codeVerifier?: string
    ): Promise<AuthResult> => {
      recordAuthApiStep("api.exchange_access_token", {
        flow: "authenticateWithCode",
        codePrefix: `${code.substring(0, 6)}…`,
        hasCodeVerifier: Boolean(codeVerifier),
      })

      try {
        const response = await exchangePartnerAccessToken(
          "authenticateWithCode",
          code,
          codeVerifier
        )

        return {
          accessToken: parseAccessToken(response?.access_token),
          deviceId: get(response, "unique_device_id"),
        }
      } catch (error) {
        log({
          category: "auth-api",
          level: "error",
          message: "Failed to authenticate with code",
          data: { error, code: `${code.substring(0, 10)}...` },
        })
        captureAuthApiFailure("api.exchange_access_token", error, {
          flow: "authenticateWithCode",
          hasCodeVerifier: Boolean(codeVerifier),
        })
      }
    },

    authenticateFromParams: async (): Promise<AuthResult> => {
      const searchParams = new URLSearchParams(window.location.search)
      const urlSummary = summarizeAuthUrlParams(searchParams)
      recordAuthApiStep("api.authenticate_from_params", urlSummary)

      try {
        const code = searchParams.get("code")
        const codeVerifier = searchParams.get("code_verifier")

        if (code) {
          recordAuthApiStep("api.exchange_access_token", {
            flow: "oauth",
            codePrefix: `${code.substring(0, 6)}…`,
            hasCodeVerifier: Boolean(codeVerifier),
          })

          const response = await exchangePartnerAccessToken(
            "oauth",
            code,
            codeVerifier || undefined
          )
          return {
            accessToken: parseAccessToken(response?.access_token),
            deviceId: get(response, "unique_device_id"),
          }
        }

        // Internal testing flow
        const partnerId = Number(searchParams.get("partner_id") ?? 1)
        const partnerSecret = searchParams.get("partner_secret") ?? "test"
        const phoneNo = searchParams.get("phone_no")
        const fullname = searchParams.get("fullname") ?? "VTM"

        if (!phoneNo) {
          throw new Error("Phone number is required for internal testing")
        }

        // Validate sensitive parameters
        if (partnerSecret === "test" && partnerId === 1) {
          log({
            category: "auth-api",
            level: "warning",
            message: "Using default test credentials",
          })
        }

        const validatedPhoneNo = validatePhoneNumber(phoneNo)

        recordAuthApiStep("api.get_partner_code", {
          flow: "internal-testing",
          partnerId,
          phoneSuffix: validatedPhoneNo.slice(-4),
        })

        const authCodeResponse = await requestPartnerAuthorizeCode(
          "internal-testing",
          {
            partner_id: partnerId,
            partner_name: "be",
            partner_secret: partnerSecret,
            user: {
              phone_no: validatedPhoneNo,
              fullname,
              user_id: validatedPhoneNo,
            },
          }
        )

        const authCode = get(authCodeResponse, "authorization_code", null)
        if (!authCode) {
          throw new Error("No authorization code received")
        }

        recordAuthApiStep("api.exchange_access_token", {
          flow: "internal-testing",
          codePrefix: `${String(authCode).substring(0, 6)}…`,
        })

        const response = await exchangePartnerAccessToken(
          "internal-testing",
          authCode,
          codeVerifier || undefined
        )
        return {
          accessToken: parseAccessToken(response?.access_token),
          deviceId: get(response, "unique_device_id"),
        }
      } catch (error) {
        log({
          category: "auth-api",
          level: "error",
          message: "Failed to authenticate from URL parameters",
          data: { error, ...urlSummary },
        })
        captureAuthApiFailure("api.authenticate_from_params", error, urlSummary)
      }
    },

    getAuthCode: async (params: AuthCodeParams): Promise<string> => {
      try {
        const validatedPhoneNo = validatePhoneNumber(params.phoneNo!)

        const data = await requestPartnerAuthorizeCode("getAuthCode", {
          partner_id: params.partnerId || 1,
          partner_name: "be",
          partner_secret: params.partnerSecret || "test",
          user: {
            phone_no: validatedPhoneNo,
            fullname: params.fullname || "User",
            user_id: validatedPhoneNo,
          },
        })

        const authCode = get(data, "authorization_code", null)

        if (!authCode) {
          throw new Error("No authorization code received")
        }

        return authCode
      } catch (error) {
        log({
          category: "auth-api",
          level: "error",
          message: "Failed to get auth code",
          data: {
            error,
            partnerId: params.partnerId,
            phoneNo: `${params.phoneNo?.substring(0, 3)}***`,
          },
        })
        throw error
      }
    },
  },

  location: {
    getCurrentPosition: async (
      options?: LocationOptions
    ): Promise<Coordinates> => {
      // Check URL params first (for partner integration)
      const params = new URLSearchParams(window.location.search)
      const lat = params.get("latitude")
      const lng = params.get("longitude")

      if (lat && lng) {
        return {
          latitude: parseFloat(lat),
          longitude: parseFloat(lng),
        }
      }

      // Fallback to browser geolocation
      if (!navigator?.geolocation?.getCurrentPosition) {
        throw {
          code: 4,
          message: "Geolocation is not supported by this browser",
        }
      }

      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
            })
          },
          (error) => {
            log({
              category: "location-permission",
              level: "error",
              message: "Failed to get location",
              data: error,
            })
            reject(error)
          },
          {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: options?.timeout || 10000,
            ...options,
          }
        )
      })
    },
  },

  storage: {
    getItem: async (key: string): Promise<string | null> => {
      try {
        return localStorage.getItem(key)
      } catch (error) {
        log({
          category: "storage",
          level: "error",
          message: "Failed to get storage item",
          data: { key, error },
        })
        return null
      }
    },

    setItem: async (key: string, value: string): Promise<void> => {
      try {
        localStorage.setItem(key, value)
      } catch (error) {
        log({
          category: "storage",
          level: "error",
          message: "Failed to set storage item",
          data: { key, error },
        })
      }
    },

    removeItem: async (key: string): Promise<void> => {
      try {
        localStorage.removeItem(key)
      } catch (error) {
        log({
          category: "storage",
          level: "error",
          message: "Failed to remove storage item",
          data: { key, error },
        })
      }
    },

    clear: async (): Promise<void> => {
      try {
        localStorage.clear()
      } catch (error) {
        log({
          category: "storage",
          level: "error",
          message: "Failed to clear storage",
          data: error,
        })
      }
    },
  },

  ui: {
    hideKeyboard: async (): Promise<void> => {
      // Focus out from current element
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },

    openWebview: async (url: string): Promise<void> => {
      window.open(url, "_blank")
    },

    closeLoading: async (): Promise<void> => {
      // No-op for web, could dispatch event if needed
    },
  },

  permissions: {
    getSetting: async (): Promise<PermissionSettings> => {
      try {
        // Web Permissions API
        let locationStatus: PermissionStatus = "prompt"

        if ("permissions" in navigator) {
          const locationPerm = await navigator.permissions.query({
            name: "geolocation",
          })
          locationStatus = locationPerm.state as PermissionStatus
        }

        return {
          location: locationStatus,
          phone: "granted", // Always granted for tel: links
          userInfo: "granted", // No permission needed on web
        }
      } catch (_error) {
        return {
          location: "prompt",
          phone: "granted",
          userInfo: "granted",
        }
      }
    },

    checkPermission: async (permission: string): Promise<PermissionStatus> => {
      if (permission === "geolocation" && "permissions" in navigator) {
        try {
          const result = await navigator.permissions.query({
            name: "geolocation",
          })
          return result.state as PermissionStatus
        } catch (_error) {
          return "prompt"
        }
      }
      return "granted"
    },
  },

  network: {
    onStatusChange: (callback: (status: NetworkStatus) => void) => {
      const handler = () => {
        const connection = (navigator as any).connection
        callback({
          isConnected: navigator.onLine,
          networkType: connection?.effectiveType || "unknown",
        })
      }

      window.addEventListener("online", handler)
      window.addEventListener("offline", handler)

      // Return unsubscribe function
      return () => {
        window.removeEventListener("online", handler)
        window.removeEventListener("offline", handler)
      }
    },
  },
}

export function registerAppViewPlatform() {
  registerPlatform(appViewPlatform)
}
